import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  opacity: number;
  pulsePhase: number;
}

interface ParticleNetworkProps {
  className?: string;
}

export default function ParticleNetwork({ className = '' }: ParticleNetworkProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const mouseRef = useRef({ x: -9999, y: -9999 });
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    // Defer start by 300ms so it does not compete with LCP rendering
    const startTimeout = setTimeout(startAnimation, 300);

    function startAnimation() {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      let width = 0;
      let height = 0;
      let particles: Particle[] = [];
      // Fewer nodes on mobile — reduces CPU load significantly
      const NODE_COUNT = window.innerWidth < 768 ? 25 : 50;
      const CONNECTION_DISTANCE = 140;
      const MOUSE_REPEL_DIST = 120;

      const isDark = () => document.documentElement.classList.contains('dark');

      const resize = () => {
        const dpr = window.devicePixelRatio || 1;
        width = canvas.offsetWidth;
        height = canvas.offsetHeight;
        canvas.width = width * dpr;
        canvas.height = height * dpr;
        ctx.scale(dpr, dpr);
      };

      const createParticle = (): Particle => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
        radius: Math.random() * 2.5 + 1,
        opacity: Math.random() * 0.5 + 0.3,
        pulsePhase: Math.random() * Math.PI * 2,
      });

      const init = () => {
        resize();
        particles = Array.from({ length: NODE_COUNT }, createParticle);
      };

      const draw = (time: number) => {
        ctx.clearRect(0, 0, width, height);

        const dark = isDark();
        const nodeColor = dark ? '110,231,183' : '5,150,105';
        const lineColor = dark ? '52,211,153' : '16,185,129';

        particles.forEach(p => {
          p.x += p.vx;
          p.y += p.vy;

          if (p.x < -20) p.x = width + 20;
          if (p.x > width + 20) p.x = -20;
          if (p.y < -20) p.y = height + 20;
          if (p.y > height + 20) p.y = -20;

          const dx = p.x - mouseRef.current.x;
          const dy = p.y - mouseRef.current.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < MOUSE_REPEL_DIST && dist > 0) {
            const force = (MOUSE_REPEL_DIST - dist) / MOUSE_REPEL_DIST;
            p.vx += (dx / dist) * force * 0.04;
            p.vy += (dy / dist) * force * 0.04;
          }

          const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
          const maxSpeed = 0.9;
          if (speed > maxSpeed) {
            p.vx = (p.vx / speed) * maxSpeed;
            p.vy = (p.vy / speed) * maxSpeed;
          }

          p.vx *= 0.998;
          p.vy *= 0.998;

          const pulse = 0.5 + 0.5 * Math.sin(time * 0.001 + p.pulsePhase);
          const finalOpacity = p.opacity * (0.7 + 0.3 * pulse);

          ctx.beginPath();
          ctx.arc(p.x, p.y, p.radius * (1 + pulse * 0.3), 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${nodeColor},${finalOpacity})`;
          ctx.fill();
        });

        for (let i = 0; i < particles.length; i++) {
          for (let j = i + 1; j < particles.length; j++) {
            const dx = particles[i].x - particles[j].x;
            const dy = particles[i].y - particles[j].y;
            const dist = Math.sqrt(dx * dx + dy * dy);

            if (dist < CONNECTION_DISTANCE) {
              const alpha = (1 - dist / CONNECTION_DISTANCE) * 0.35;
              ctx.beginPath();
              ctx.moveTo(particles[i].x, particles[i].y);
              ctx.lineTo(particles[j].x, particles[j].y);
              ctx.strokeStyle = `rgba(${lineColor},${alpha})`;
              ctx.lineWidth = 1;
              ctx.stroke();
            }
          }
        }

        animFrameRef.current = requestAnimationFrame(draw);
      };

      const onMouseMove = (e: MouseEvent) => {
        const rect = canvas.getBoundingClientRect();
        mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      };

      const onMouseLeave = () => {
        mouseRef.current = { x: -9999, y: -9999 };
      };

      init();
      animFrameRef.current = requestAnimationFrame(draw);

      const resizeObs = new ResizeObserver(init);
      resizeObs.observe(canvas);

      canvas.addEventListener('mousemove', onMouseMove);
      canvas.addEventListener('mouseleave', onMouseLeave);

      cleanupRef.current = () => {
        cancelAnimationFrame(animFrameRef.current);
        resizeObs.disconnect();
        canvas.removeEventListener('mousemove', onMouseMove);
        canvas.removeEventListener('mouseleave', onMouseLeave);
      };
    }

    return () => {
      clearTimeout(startTimeout);
      cleanupRef.current?.();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full ${className}`}
    />
  );
}
